import { DEFAULT_DEBOUNCE_DELAY, STATUSPAGE } from '@repo/ui';
import debounce from 'debounce';
import { TceManagementUseCaseInterface } from 'domain/usecases';
import { ORDER_TYPE } from 'domain/value-objects/order-type';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import { useCallback, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';

interface IPaginationMetadata {
  selectedPage: number;
  rowsPerPage: number;
  orderColumn: 'STUDENT_NAME' | 'GROUP_NAME' | 'START_DATE' | 'END_DATE';
  sortDirection: ORDER_TYPE;
  searchString: string;
}

export const useTceManagementList = () => {
  const { tceSummaryService } = useService();

  const [tceManagementData, setTceManagementData] =
    useState<TceManagementUseCaseInterface.Result>();
  const [listLoadingStatus, setListLoadingStatus] = useState(
    STATUSPAGE.LOADING,
  );
  const [paginationMetadata, setPaginationMetadata] =
    useState<IPaginationMetadata>({
      selectedPage: 0,
      rowsPerPage: 10,
      orderColumn: 'STUDENT_NAME',
      sortDirection: ORDER_TYPE.ASC,
      searchString: '',
    });

  const changePaginationMetadata = <T extends keyof IPaginationMetadata>(
    key: T,
    value: IPaginationMetadata[T],
  ) => {
    setPaginationMetadata((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handlePageChange = useCallback((page: number) => {
    changePaginationMetadata('selectedPage', page - 1);
  }, []);

  const handleRowsPerPageChange = useCallback((rows: number) => {
    changePaginationMetadata('rowsPerPage', rows);
  }, []);

  const handleSortRemote = useCallback(
    (
      column: TableColumn<TceManagementUseCaseInterface.Result['tces'][0]>,
      sortDirection: SortOrder,
    ) => {
      const columnMap: Record<
        string,
        'STUDENT_NAME' | 'GROUP_NAME' | 'START_DATE' | 'END_DATE'
      > = {
        studentName: 'STUDENT_NAME',
        groupName: 'GROUP_NAME',
        startDate: 'START_DATE',
        endDate: 'END_DATE',
      };

      changePaginationMetadata(
        'sortDirection',
        sortDirection.toUpperCase() as ORDER_TYPE,
      );
      changePaginationMetadata(
        'orderColumn',
        columnMap[column.id as string] || 'STUDENT_NAME',
      );
    },
    [],
  );

  const handleSearchRemote = useCallback((searchString: string) => {
    changePaginationMetadata('searchString', searchString);
  }, []);

  const requestGetTceManagement = useCallback(
    async (params: TceManagementUseCaseInterface.Params) => {
      setListLoadingStatus(STATUSPAGE.LOADING);
      try {
        const response = await tceSummaryService.tceManagement(params);
        setTceManagementData(response);
      } catch (error) {
        notifyError(error);
        setTceManagementData(undefined);
      } finally {
        setListLoadingStatus(STATUSPAGE.DONE);
      }
    },
    [tceSummaryService],
  );

  const debouncedRequestGetTceManagement = useCallback(
    debounce(requestGetTceManagement, DEFAULT_DEBOUNCE_DELAY),
    [requestGetTceManagement],
  );

  return {
    tceManagementData,
    paginationMetadata,
    listLoadingStatus,
    setListLoadingStatus,
    handlePageChange,
    handleRowsPerPageChange,
    handleSortRemote,
    handleSearchRemote,
    requestGetTceManagement: debouncedRequestGetTceManagement,
  };
};
